import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useMemo } from "react";

import { DEFAULT_BASE_CURRENCY } from "@/constants/accounts";
import { PRICES_POLLING_INTERVAL_MS } from "@/constants/polling";
import { shouldPollOnPage } from "@/hooks/usePollingPage";
import { isTestEnv } from "@/lib/env";
import { useGetPricesQuery } from "@generated/redux/services/generated/api";
import { FilteredTickerPrice } from "@generated/types";

import { useCustomerAccount } from "../CustomerAccountProvider";

import {
  buildPricesMap,
  convertToBaseCurrency as convertToBaseCurrencyUtil,
  getPrice as getPriceUtil,
} from "./TickerProvider.utils";

interface TickerContextValue {
  prices: FilteredTickerPrice[] | null;
  baseCurrency: string;
  convertToBaseCurrency: (amount: number, currency: string) => number;
  getPrice: (
    fromCurrency: string,
    toCurrency: string
  ) => FilteredTickerPrice | null;
  isPricesLoading: boolean;
  isPricesError: boolean;
  refetchPrices: () => void;
}

const TickerContext = createContext<TickerContextValue>({
  prices: null,
  baseCurrency: DEFAULT_BASE_CURRENCY,
  convertToBaseCurrency: () => 0,
  getPrice: () => null,
  isPricesLoading: false,
  isPricesError: false,
  refetchPrices: () => {},
});

interface TickerProviderProps {
  children: React.ReactNode;
  pollingInterval?: number; // Custom polling interval in milliseconds
  baseCurrencyOverride?: string; // Optional base currency override
}

const TickerProvider = ({
  children,
  pollingInterval,
  baseCurrencyOverride,
}: TickerProviderProps) => {
  const { customerAccount, customerAccountBaseCurrency } = useCustomerAccount();

  const baseCurrency = baseCurrencyOverride || customerAccountBaseCurrency;
  const pathname = usePathname();
  const shouldPoll = shouldPollOnPage(pathname);

  const skipQuery =
    pollingInterval === 0 || !customerAccount?.customerAccount?.id;

  // In test env, disable polling to keep the network quiescent for E2E tests.
  // pollingInterval === 0 (Storybook) still skips the query entirely via skipQuery above.
  // Only poll on dashboard/accounts pages to avoid unnecessary requests elsewhere.
  const effectivePollingInterval =
    isTestEnv || !shouldPoll
      ? undefined
      : pollingInterval || PRICES_POLLING_INTERVAL_MS;

  const {
    data: pricesData,
    isLoading: isPricesLoading,
    isError: isPricesError,
    refetch: refetchPrices,
  } = useGetPricesQuery(
    { baseProduct: baseCurrency },
    {
      skip: skipQuery,
      pollingInterval: effectivePollingInterval,
      skipPollingIfUnfocused: true,
    }
  );

  // Preserve RTK Query's referential stability — no .map() or spread needed.
  // The previous identity transform (.map(p => ({...p, bid: p.bid, ask: p.ask})))
  // created new objects every render, breaking the memoization chain downstream.
  const prices = useMemo(
    () => (pollingInterval === 0 ? [] : (pricesData?.data ?? null)),
    [pollingInterval, pricesData?.data]
  );

  // Create a map for quick price lookups
  const pricesMap = useMemo(() => buildPricesMap(prices), [prices]);

  // Function to get price between any two currencies (with division-by-zero guards)
  const getPrice = useCallback(
    (fromCurrency: string, toCurrency: string): FilteredTickerPrice | null =>
      getPriceUtil(pricesMap, fromCurrency, toCurrency),
    [pricesMap]
  );

  // Convert any currency to base currency using the bid price
  const convertToBaseCurrency = useCallback(
    (amount: number, currency: string): number =>
      convertToBaseCurrencyUtil(amount, currency, baseCurrency, pricesMap),
    [baseCurrency, pricesMap]
  );

  const value = useMemo(
    () => ({
      prices,
      baseCurrency,
      convertToBaseCurrency,
      getPrice,
      isPricesLoading,
      isPricesError,
      refetchPrices,
    }),
    [
      prices,
      baseCurrency,
      convertToBaseCurrency,
      getPrice,
      isPricesLoading,
      isPricesError,
      refetchPrices,
    ]
  );

  return (
    <TickerContext.Provider value={value}>{children}</TickerContext.Provider>
  );
};

export default TickerProvider;

export const useTicker = () => {
  const context = useContext(TickerContext);
  if (!context) {
    throw new Error("useTicker must be used within a TickerProvider");
  }
  return context;
};
